import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { Attributes } from '../../../../models/api/view/claim/details/attributes/attributes';
import { ReservesSummary } from '../../../../models/api/view/claim/financials/reserves/reserves-summary';
import { HighlightPipe } from '../../../../pipes/highlight.pipe';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ExpandPanelComponent } from '../../../core/panels/expand-panel.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { getExpandDateOnly, getExpandSubject } from '../../../../functions/get-expand-subject';
import { ReservesAttributesTabComponent } from './reserves-attributes-tab.component';
import { ReservesExpenseTabComponent } from './reserves-expense-tab.component';
import { ReservesIndemnityTabComponent } from './reserves-indemnity-tab.component';
import { ReservesMedicalTabComponent } from './reserves-medical-tab.component';
import { ReservesUnallocatedExpenseTabComponent } from './reserves-unallocated-expense-tab.component';

@Component({
  selector: 'app-reserves',
  imports: [
    TableComponent,
    ColumnComponent,
    HighlightPipe,
    ExpandPanelComponent,
    CommonModule,
    PageHeaderComponent,
    TabPanelComponent,
    TabComponent,
    ReservesIndemnityTabComponent,
    ReservesMedicalTabComponent,
    ReservesExpenseTabComponent,
    ReservesUnallocatedExpenseTabComponent,
    ReservesAttributesTabComponent,
  ],
  template: `
    <app-page-header label="Reserves" />
    @if (!au && !gl && !pr) {
      <app-table
        #reservesTable
        [dataSource]="reservesSummary"
        [scrollable]="false"
        recordLabel="Reserve"
        [expandable]="true"
        [hideExpandAll]="true"
        [rowExpandLabel]="getReserveRowExpandLabel"
        [sortOrder]="-1"
        sortField="date"
        dataKey="number"
        [stripedRows]="false"
      >
        <app-column label="Date" name="date" format="d" />
        <app-column label="Number" name="number" />
        <app-column label="Created by" name="createdBy" />
        <app-column label="Indemnity" name="indemnity" format="c" />
        <app-column label="Medical" name="medical" format="c" />
        <app-column label="Expense" name="expense" format="c" />
        <app-column label="Other" name="other" format="c" />
        <app-column label="Total" name="total" format="c" />
        @if (workersComp && unallocatedExpense) {
          <app-column
            label="Unallocated expense"
            name="unallocatedExpense"
            format="c"
          />
          <app-column label="Combined totals" name="combinedTotal" format="c" />
        }
        <app-column name="comments" [fullRow]="true" [fullRowLabel]="false">
          <ng-template #item let-row>
            <app-expand-panel
              [expanded]="reservesTable.expanded"
              [label]="
                getExpandSubject('Reserve', getExpandDateOnly(row.date)) ||
                'Reserve'
              "
              collapseHeight="114px"
            >
              <span
                style="white-space: pre-line; word-break: break-word; overflow-wrap: anywhere;"
                [innerHTML]="
                  row.comments | highlight: reservesTable.searchValue
                "
              ></span>
            </app-expand-panel>
          </ng-template>
        </app-column>
        <ng-template #expand let-data>
          <app-tab-panel class="tw-grow" classes="tw-min-h-[360px]">
            <app-tab label="Indemnity">
              <ng-template #content>
                <app-reserves-indemnity-tab [reserveNumber]="data.number" />
              </ng-template>
            </app-tab>
            <app-tab label="Medical">
              <ng-template #content>
                <app-reserves-medical-tab [reserveNumber]="data.number" />
              </ng-template>
            </app-tab>
            <app-tab label="Expense">
              <ng-template #content>
                <app-reserves-expense-tab [reserveNumber]="data.number" />
              </ng-template>
            </app-tab>
            @if (workersComp && unallocatedExpense) {
              <app-tab label="Unallocated expense">
                <ng-template #content>
                  <app-reserves-unallocated-expense-tab
                    [reserveNumber]="data.number"
                  />
                </ng-template>
              </app-tab>
            }
            @if (data.attributes.length) {
              <app-tab label="Attributes">
                <ng-template #content>
                  <app-reserves-attributes-tab
                    [workersComp]="workersComp"
                    [attributes]="data.attributes"
                  />
                </ng-template>
              </app-tab>
            }
          </app-tab-panel>
        </ng-template>
      </app-table>
    }
    @if (au || gl || pr) {
      <app-table
        #reservesTable
        [dataSource]="reservesSummary"
        [scrollable]="false"
        recordLabel="Reserve"
        dataKey="number"
        [sortOrder]="-1"
        sortField="date"
        [stripedRows]="false"
      >
        <app-column label="Date" name="date" format="d" />
        <app-column label="Number" name="number" />
        <app-column label="Created by" name="createdBy" />
        <app-column label="Loss" name="loss" format="c" />
        <app-column label="Expense" name="expense" format="c" />
        <app-column label="Total" name="total" format="c" />
        @if (unallocatedExpense) {
          <app-column
            label="Unallocated expense"
            name="unallocatedExpense"
            format="c"
          />
          <app-column label="Combined totals" name="combinedTotal" format="c" />
        }
        <app-column name="comments" [fullRow]="true" [fullRowLabel]="false">
          <ng-template #item let-row>
            <app-expand-panel
              [expanded]="reservesTable.expanded"
              [label]="
                getExpandSubject('Reserve', getExpandDateOnly(row.date)) ||
                'Reserve'
              "
              collapseHeight="114px"
            >
              <span
                style="white-space: pre-line; word-break: break-word; overflow-wrap: anywhere;"
                [innerHTML]="
                  row.comments | highlight: reservesTable.searchValue
                "
              ></span>
            </app-expand-panel>
          </ng-template>
        </app-column>
      </app-table>
    }
  `,
  styles: [``],
})
export class ReservesComponent extends BaseComponent implements OnInit {
  reservesSummary: ReservesSummary[] = [];
  attributes: Attributes[] = [];
  unallocatedExpense = false;
  disability = false;
  workersComp = false;
  au = false;
  gl = false;
  pr = false;

  protected getExpandSubject = getExpandSubject;
  protected getExpandDateOnly = getExpandDateOnly;

  /** Left row-expand opens detail tabs — prefer date over reserve number. */
  protected getReserveRowExpandLabel = (row: any) =>
    getExpandSubject('Reserve', getExpandDateOnly(row?.date)) || 'Reserve';

  async ngOnInit() {
    this.view.showLoading();
    const claim = this.view.currentClaim();
    this.disability = claim.lineOfBusiness === 'DS';
    this.workersComp = claim.lineOfBusiness === 'WC';
    this.au = claim.lineOfBusiness === 'AU';
    this.gl = claim.lineOfBusiness === 'GL';
    this.pr = claim.lineOfBusiness === 'PR';
    this.unallocatedExpense = this.session.authorize('UnallocatedExpense');

    const reserves = await this.api.reserves.reservesSummary(getClaimKey());
    this.reservesSummary = reserves;

    this.view.hideLoading();
  }
}
